// An expiry is entered as a month and a year — owner ruling, 6 September 2026,
// spec 041. AGE ships no month-only entry: `mud-date-input` offers exactly
// DD/MM/YYYY, MM/DD/YYYY and YYYY-MM-DD and refuses to commit anything short of
// a whole date, and `mud-date-picker` renders a month grid only as private view
// state, with every mode emitting an ISO day. So the field is built in AGE's
// idiom over `mud-text-input`, which is also what the wireframe draws
// (wireframes/react/src/pages/raport-nou/piese.tsx:744 — a masked field with the
// LL/AAAA placeholder and numeric input mode). Catalogued in
// docs/artifacts/age-gap-catalogue.html.
import { useState } from 'react';
import { MudTextInput } from '@egov-moldova/mud-react';
import {
  isEndOfMonth,
  maskMonthInput,
  monthInputToWireDate,
  monthInputToWireMonth,
  wireDateToMonthInput,
} from './date-format';

const ISO_FULL_DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface ExpiryMonthInputProps {
  /** Wire value, `YYYY-MM-DD` or a bare `YYYY-MM`. Empty string when nothing is entered yet. */
  value: string;
  /**
   * The wire value, or '' while the month is incomplete: the month's last day
   * (`YYYY-MM-DD`), or the month itself (`YYYY-MM`) with `emits="month"`.
   */
  onChange: (wire: string) => void;
  /**
   * What a typed month is sent as. `month` sends `YYYY-MM`, which core stores as
   * reported (precision `MONTH`) and every screen shows as `LL/AAAA`: the reporting
   * dialog. `lastDay` (default) completes it to the month's last day, for the
   * correction screens, whose requests carry a date and no precision.
   */
  emits?: 'month' | 'lastDay' | undefined;
  size?: 'md' | 'lg' | undefined;
  invalid?: boolean | undefined;
  disabled?: boolean | undefined;
  ariaLabel?: string | undefined;
  placeholder?: string | undefined;
  /**
   * Shown under the field when `value` is a day somebody chose mid-month —
   * already translated and already carrying the date, since only the caller
   * knows which sentence its screen wants.
   */
  storedDayNote?: string | undefined;
  label?: string | undefined;
  helperText?: string | undefined;
  /** Shown under the field; a field that shows an error is also marked invalid. */
  errorText?: string | undefined;
  /**
   * Locks the field: the expiry of a lot already reported changes only through
   * a correction request (spec 054 FR-012, UC51), so a locked field emits nothing.
   */
  readonly?: boolean | undefined;
  /**
   * Marks the field mandatory. Passed to `mud-text-input`, which draws AGE's red
   * asterisk after the label and makes the inner input `required`; the label
   * text carries no asterisk of its own.
   */
  required?: boolean | undefined;
}

/**
 * The month field. Its whole reason for existing beyond a masked input is the
 * second rule of spec 041: a row that already holds a real mid-month day must
 * not be moved to the end of the month just because someone opened the screen.
 *
 * So the field renders the month of whatever it is given, and emits **only**
 * from a keystroke. Untouched, the value it was handed is the value that is
 * still there on save. The moment the operator types, they have asserted a
 * month, and the month resolves to its last day — including when it is the same
 * month the stored day sits in.
 */
export function ExpiryMonthInput({
  value,
  onChange,
  emits = 'lastDay',
  size = 'md',
  invalid,
  disabled,
  ariaLabel,
  placeholder = 'LL/AAAA',
  storedDayNote,
  label,
  helperText,
  errorText,
  readonly,
  required,
}: ExpiryMonthInputProps): JSX.Element {
  // Held only while the operator is mid-entry: '03/2' is not a month and has no
  // wire value to be derived from, so it cannot live in `value`.
  const [typed, setTyped] = useState<string | null>(null);
  const display = typed ?? wireDateToMonthInput(value);

  // A day the operator did not choose is worth saying out loud, because the
  // next keystroke in this field is what would replace it.
  const showsStoredDay =
    typed === null &&
    storedDayNote !== undefined &&
    ISO_FULL_DATE.test(value) &&
    !isEndOfMonth(value);

  return (
    <span className="simsm-expiry-month">
      <MudTextInput
        size={size}
        inputmode="numeric"
        maxLength={7}
        value={display}
        placeholder={placeholder}
        invalid={Boolean(invalid) || (errorText !== undefined && errorText !== '')}
        disabled={Boolean(disabled)}
        readonly={Boolean(readonly)}
        required={Boolean(required)}
        {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
        {...(label === undefined ? {} : { label })}
        {...(helperText === undefined ? {} : { helperText })}
        {...(errorText === undefined ? {} : { errorText })}
        onMudInput={(e: CustomEvent<{ value: string }>) => {
          if (readonly) return;
          // Masking only as the field grows: rewriting on delete would put the
          // separator straight back and make backspace do nothing.
          const raw = e.detail.value;
          const next = raw.length > display.length ? maskMonthInput(raw) : raw;
          setTyped(next);
          onChange(emits === 'month' ? monthInputToWireMonth(next) : monthInputToWireDate(next));
        }}
      />
      {showsStoredDay ? <span className="simsm-expiry-month__stored">{storedDayNote}</span> : null}
    </span>
  );
}
